import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  fetchMovie,
  assignAuditorium,
  getReservedSeats,
  createCheckoutIntent,
  confirmCheckout,
  SEAT_ROWS,
  SEATS_PER_ROW,
  TICKET_PRICE_CENTS,
} from '../api/starviewApi';
import { getLocalPrice, formatMoney, stripeAmountToMajor, localizeDigits } from '../utils/localPricing';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { getShowtimeKey } from '../utils/showtimeKey';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getSeatLabel(row, seat) {
  return `${String.fromCharCode(65 + row)}${seat + 1}`;
}

export default function TheaterRoom() {
  const { t } = useTranslation();
  const query = useQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, authFetch } = useAuth();

  const movieId = query.get('movieId');
  const selectedTime = query.get('time');
  // Locked to `selectedTime` (not recomputed on every render) so the same
  // calendar date is used consistently for assignAuditorium,
  // getReservedSeats, and confirmCheckout below - see utils/showtimeKey.js.
  const showtimeKey = useMemo(() => (selectedTime ? getShowtimeKey(selectedTime) : null), [selectedTime]);
  const [movie, setMovie] = useState(location.state?.movie || null);
  const [auditorium, setAuditorium] = useState(null);
  const [reservedSeats, setReservedSeats] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [step, setStep] = useState('seats'); // 'seats' | 'payment'
  const [clientSecret, setClientSecret] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  // This market's own price card: local currency, local multiplex-normal
  // amount, local payment rails. $11.50-in-USD-for-everyone is over.
  const [localCard, setLocalCard] = useState(null);
  useEffect(() => {
    if (movie?.country) getLocalPrice(movie.country).then(setLocalCard);
  }, [movie?.country]);
  const fmt = (stripeAmt, ccy) => formatMoney(stripeAmountToMajor(stripeAmt, ccy), ccy, movie?.country);
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const cardElementRef = useRef(null);
  const cardMountRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/Login', { state: { from: `${location.pathname}${location.search}` } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !movieId || !selectedTime) {
      if (!movieId || !selectedTime) {
        setError('No movie or showtime selected.');
        setLoading(false);
      }
      return;
    }
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        let movieData = movie;
        if (!movieData) {
          movieData = await fetchMovie(movieId);
          if (cancelled) return;
          setMovie(movieData);
        }
        const aud = await assignAuditorium(authFetch, {
          moviePublicId: movieId,
          country: movieData.country,
          theaterNumber: movieData.theaterNumber,
          showtime: showtimeKey,
        });
        if (cancelled) return;
        setAuditorium(aud);
        const reserved = await getReservedSeats(authFetch, {
          movieId,
          showtime: showtimeKey,
          auditoriumId: aud.auditoriumId,
        });
        if (cancelled) return;
        setReservedSeats(reserved);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load seats.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, movieId, selectedTime, showtimeKey]);

  const toggleSeat = (seatId) => {
    if (reservedSeats.includes(seatId)) return;
    setSelectedSeats((prev) =>
      prev.includes(seatId) ? prev.filter((id) => id !== seatId) : [...prev, seatId]
    );
  };

  const totalCents = selectedSeats.length * TICKET_PRICE_CENTS;

  const handleProceedToPayment = async () => {
    if (selectedSeats.length === 0) return;
    setPayError('');
    setPaying(true);
    try {
      const intent = await createCheckoutIntent(authFetch, {
        seats: selectedSeats,
        promoCode: promoCode.trim() || undefined,
        giftCardCode: giftCardCode.trim() || undefined,
        country: movie?.country,
      });
      setClientSecret(intent.clientSecret);
      setPriceBreakdown(intent);
      stripeRef.current = await loadStripe(intent.publishableKey);
      setStep('payment');
    } catch (err) {
      setPayError(err.message || 'Failed to start checkout.');
    } finally {
      setPaying(false);
    }
  };

  // Mounts the real Stripe card element once we're on the payment step and
  // have a live Stripe instance - torn down again if the user goes back.
  useEffect(() => {
    if (step !== 'payment' || !stripeRef.current || !cardMountRef.current) return;
    const elements = stripeRef.current.elements();
    const card = elements.create('card', {
      style: { base: { color: '#fff', fontSize: '16px', '::placeholder': { color: 'rgba(255,255,255,0.4)' } } },
    });
    card.mount(cardMountRef.current);
    elementsRef.current = elements;
    cardElementRef.current = card;
    return () => {
      card.unmount();
    };
  }, [step]);

  const handlePay = async () => {
    if (!stripeRef.current || !cardElementRef.current) return;
    setPayError('');
    setPaying(true);
    try {
      const result = await stripeRef.current.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElementRef.current },
      });
      if (result.error) {
        throw new Error(result.error.message || 'Payment failed.');
      }
      if (result.paymentIntent.status !== 'succeeded') {
        throw new Error(`Payment not completed (status: ${result.paymentIntent.status})`);
      }
      const confirmation = await confirmCheckout(authFetch, {
        paymentIntentId: result.paymentIntent.id,
        movieId,
        movieTitle: movie.title,
        country: movie.country,
        seats: selectedSeats,
        showtime: showtimeKey || selectedTime,
        auditoriumId: auditorium.auditoriumId,
        promoCode: promoCode.trim() || undefined,
        giftCardCode: giftCardCode.trim() || undefined,
      });
      navigate('/Success', {
        state: {
          tickets: confirmation.tickets,
          movieTitle: movie.title,
          showtime: selectedTime,
          auditorium,
        },
      });
    } catch (err) {
      setPayError(err.message || 'Payment failed.');
    } finally {
      setPaying(false);
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return <div style={styles.page}><p style={styles.time}>{t('theaterRoom.loadingSeats')}</p></div>;
  }

  if (error) {
    return <div style={styles.page}><p style={styles.time}>{error}</p></div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.title}>{movie?.title || 'Movie'}</h1>
        <p style={styles.time}>
          {selectedTime} • {auditorium?.format === 'IMAX' ? 'IMAX' : 'Digital'} • Auditorium {auditorium?.auditoriumNumber}
        </p>

        {step === 'seats' && (
          <>
            <div style={styles.screen}>SCREEN</div>
            <div style={styles.seatMap}>
              {Array.from({ length: SEAT_ROWS }).map((_, row) => (
                <div key={row} style={styles.seatRow}>
                  <span style={styles.rowLabel}>{String.fromCharCode(65 + row)}</span>
                  {Array.from({ length: SEATS_PER_ROW }).map((_, seat) => {
                    const seatId = getSeatLabel(row, seat);
                    const isOccupied = reservedSeats.includes(seatId);
                    const isSelected = selectedSeats.includes(seatId);
                    const seatStyle = isOccupied ? styles.seatSold : isSelected ? styles.seatSelected : styles.seatFree;
                    return (
                      <button
                        key={seatId}
                        style={{ ...styles.seat, ...seatStyle }}
                        disabled={isOccupied}
                        onClick={() => toggleSeat(seatId)}
                        title={seatId}
                      >
                        {localizeDigits(seatId, movie?.country)}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <p style={styles.time}>
              {t('theaterRoom.seatSelected', { count: selectedSeats.length })}
              {selectedSeats.length > 0 && localCard ? ` — ${formatMoney(localCard.amountMajor * selectedSeats.length, localCard.currency, movie?.country)}` : totalCents > 0 ? ` — ${(totalCents / 100).toFixed(2)}` : ''}
            </p>

            {selectedSeats.length > 0 && (
              <div style={styles.promoRow}>
                <input
                  style={styles.promoInput}
                  placeholder={t('theaterRoom.promoCode')}
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
                <input
                  style={styles.promoInput}
                  placeholder={t('theaterRoom.giftCardCode')}
                  value={giftCardCode}
                  onChange={(e) => setGiftCardCode(e.target.value)}
                />
              </div>
            )}
            {selectedSeats.length > 0 && localCard?.paymentRails?.length > 0 && (
              <p style={{ opacity: 0.75, fontSize: 13, marginTop: 6 }}>
                {t('theaterRoom.payWith', { rails: localCard.paymentRails.slice(0, 3).join(' · ') })}
              </p>
            )}

            {!!payError && <p style={styles.errorText}>{payError}</p>}

            <button
              style={{ ...styles.button, ...(selectedSeats.length === 0 || paying ? styles.buttonDisabled : {}) }}
              disabled={selectedSeats.length === 0 || paying}
              onClick={handleProceedToPayment}
            >
              {paying ? t('login.pleaseWait') : t('theaterRoom.proceedToPayment')}
            </button>
          </>
        )}

        {step === 'payment' && (
          <>
            <p style={styles.time}>{selectedSeats.join(', ')}</p>
            {priceBreakdown && (
              <div style={styles.priceBreakdown}>
                <div style={styles.priceRow}><span>{t('theaterRoom.subtotal')}</span><span>{fmt(priceBreakdown.baseAmount, priceBreakdown.currency)}</span></div>
                {priceBreakdown.discountCents > 0 && (
                  <div style={styles.priceRow}>
                    <span>{t('theaterRoom.promo')} ({priceBreakdown.promoApplied?.code})</span>
                    <span>-{fmt(priceBreakdown.discountCents, priceBreakdown.currency)}</span>
                  </div>
                )}
                {priceBreakdown.giftCardAmountApplied > 0 && (
                  <div style={styles.priceRow}>
                    <span>{t('theaterRoom.giftCard')} ({priceBreakdown.giftCardApplied?.code})</span>
                    <span>-{fmt(priceBreakdown.giftCardAmountApplied, priceBreakdown.currency)}</span>
                  </div>
                )}
                <div style={{ ...styles.priceRow, ...styles.priceTotalRow }}>
                  <span>{t('theaterRoom.totalDue')}</span><span>{fmt(priceBreakdown.amount, priceBreakdown.currency)}</span>
                </div>
              </div>
            )}
            <div ref={cardMountRef} style={styles.cardElement} />
            {!!payError && <p style={styles.errorText}>{payError}</p>}
            <button
              style={{ ...styles.button, ...(paying ? styles.buttonDisabled : {}) }}
              disabled={paying}
              onClick={handlePay}
            >
              {paying ? t('theaterRoom.processing') : `${t('theaterRoom.pay')} ${priceBreakdown ? fmt(priceBreakdown.amount, priceBreakdown.currency) : localCard ? formatMoney(localCard.amountMajor * selectedSeats.length, localCard.currency, movie?.country) : `${(totalCents / 100).toFixed(2)}`}`}
            </button>
            <button style={styles.backButton} onClick={() => setStep('seats')} disabled={paying}>
              {t('theaterRoom.backToSeats')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    backgroundColor: '#001f3f',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  container: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    padding: '30px',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    width: 'min(90vw, 600px)',
  },
  title: {
    fontSize: '24px',
    color: 'white',
    marginBottom: '10px',
  },
  time: {
    fontSize: '16px',
    color: '#7FDBFF',
    marginBottom: '20px',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '14px',
    marginBottom: '12px',
  },
  screen: {
    color: 'white',
    opacity: 0.6,
    letterSpacing: '4px',
    borderBottom: '2px solid rgba(255,255,255,0.3)',
    paddingBottom: '10px',
    marginBottom: '20px',
  },
  seatMap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '20px',
    maxHeight: '40vh',
    overflowY: 'auto',
  },
  seatRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    justifyContent: 'center',
  },
  rowLabel: {
    color: 'white',
    width: '18px',
    fontSize: '12px',
  },
  seat: {
    width: '28px',
    height: '28px',
    fontSize: '9px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
  },
  seatFree: { background: 'rgba(255,255,255,0.25)', color: 'white' },
  seatSelected: { background: '#7FDBFF', color: '#001f3f', fontWeight: 'bold' },
  seatSold: { background: '#442', color: '#886', cursor: 'not-allowed' },
  promoRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  promoInput: {
    flex: 1,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(135,206,235,0.25)',
    borderRadius: '8px',
    padding: '10px 12px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
  },
  priceBreakdown: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(135,206,235,0.18)',
    borderRadius: '10px',
    padding: '12px 16px',
    marginBottom: '16px',
    textAlign: 'left',
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: 'rgba(255,255,255,0.8)',
    padding: '4px 0',
  },
  priceTotalRow: {
    borderTop: '1px solid rgba(255,255,255,0.15)',
    marginTop: '4px',
    paddingTop: '8px',
    fontWeight: 700,
    color: '#fff',
    fontSize: '15px',
  },
  cardElement: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(135,206,235,0.3)',
    borderRadius: '8px',
    padding: '14px',
    marginBottom: '16px',
    textAlign: 'left',
  },
  button: {
    background: 'linear-gradient(90deg, #6a0dad, #7FDBFF)',
    color: 'white',
    padding: '10px 20px',
    fontSize: '18px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    width: '100%',
  },
  buttonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  backButton: {
    marginTop: '12px',
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '13px',
    cursor: 'pointer',
  },
};
